/* ===========================================================================
   ROVE NEXT — the "pick 3 cards" game on the Klaviyo sign-up popup.
   13 Oct 2026.

   Klaviyo renders a generic form: a row of identical face-down card images and
   a line of rich text. Everything that makes it a game is ours: swallowing the
   first two card clicks, flipping each card to a prize face, counting picks,
   and swapping the success step's placeholder image for all three prizes.
   Without this script the popup is three inert cards, and the FIRST click
   moves the form on — in Klaviyo every card button is simply "go to next
   step". There is no Klaviyo setting for "after three picks"; this is it.

   Ported from the Rove Concepts storefront (storefront-rove-web,
   src/lib/analytics/klaviyoPick3.ts), with two differences:

   • ONE FORM, BY ID, FOR THE STORE THE COUNTRY SWITCHER SHOWS. The id comes
     from data-form-id on this script tag, which sections/rn-header.liquid
     prints from the switcher's store_country setting: THZeCs for ca, Tu7tAg
     for us. Other Klaviyo forms are left alone.

   • NO CARD-ART FILENAMES. A card is any image button in that form, on a step
     that shows at least three of them. Re-uploading the card art in the
     Klaviyo editor therefore does not switch the game off, as it would on the
     Rove Concepts version.

   What it still reads from the form design in the Klaviyo editor — change
   these there and the game breaks silently:
     - the counter line "You've won 0 / 3 offers" (either apostrophe), which
       this rewrites to 1/3, 2/3, 3/3;
     - the success step's image with alt text "Your Prize", which this
       replaces with the three prize cards.

   It listens in the CAPTURE phase and stops propagation, so Klaviyo's own
   handler never sees the first two clicks. The third is handed back on
   purpose: a real click is re-dispatched once the flip has been seen, and the
   `advancing` flag tells the handler to stand aside for it. What happens next
   (the email step, the list, the welcome flow) is the form's own design.

   The prize faces are PRIZES below, in reveal order. Styling is injected once
   from STYLE, scoped to the form, so nothing loads until the popup appears.
   =========================================================================== */
(function () {
  'use strict';

  var script = document.currentScript;
  var FORM_ID = script && script.dataset ? script.dataset.formId : '';
  if (!FORM_ID || window.__rnPick3Installed) return;
  window.__rnPick3Installed = true;

  var FORM = 'form[data-testid="klaviyo-form-' + FORM_ID + '"]';
  var PICKS_REQUIRED = 3;
  /* The third flip is left on screen this long before the form advances. */
  var ADVANCE_DELAY_MS = 1000;
  var SUMMARY_IMAGE_ALT = 'Your Prize';
  var COUNTER_RE = /You[’']ve won\s+\d+\s*\/\s*3\s+offers/gi;

  var ICON_TICKET = '<svg viewBox="0 0 64 40"><path d="M5 8H59V14C54 14 51 17 51 20S54 26 59 26V32H5V26C10 26 13 23 13 20S10 14 5 14Z" fill="none" stroke="currentColor" stroke-width="2"/><text x="32" y="25" text-anchor="middle" font-size="16" fill="currentColor">$</text></svg>';
  var ICON_TRUCK = '<svg viewBox="0 0 64 40"><path d="M4 9H38V29H4Z M38 16H50L58 24V29H38Z" fill="none" stroke="currentColor" stroke-width="2" stroke-linejoin="round"/><circle cx="15" cy="31" r="4" fill="#fbfbf9" stroke="currentColor" stroke-width="2"/><circle cx="48" cy="31" r="4" fill="#fbfbf9" stroke="currentColor" stroke-width="2"/></svg>';
  var ICON_SPHERE = '<svg viewBox="0 0 40 40"><circle cx="20" cy="20" r="15" fill="none" stroke="currentColor" stroke-width="2"/><path d="M7 15C14 19 26 19 33 15M7 25C14 21 26 21 33 25" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>';
  var LOGO = '<div class="rp-logo"><b>ROVE</b><span>NEXT</span></div>';

  /* Prize faces, in reveal order: first pick, second, third. Rendered on the
     small flip card (90x130) and the large summary card (140x202); sizes come
     from STYLE, so one markup serves both. The asterisk on the third is the
     form's own footnote. */
  var PRIZES = [
    LOGO + '<div class="rp-center"><div class="rp-value">$100</div><div class="rp-label">OFF FIRST ORDER</div><div class="rp-icon rp-ticket">' + ICON_TICKET + '</div></div>',
    LOGO + '<div class="rp-center"><div class="rp-value">FREE</div><div class="rp-label">SHIPPING</div><div class="rp-sub">ON ORDERS $100+</div><div class="rp-icon rp-truck">' + ICON_TRUCK + '</div></div>',
    LOGO + '<div class="rp-center"><div class="rp-value">50%</div><div class="rp-label">OFF SPHERE PILLOW*</div><div class="rp-icon rp-sphere">' + ICON_SPHERE + '</div></div>',
  ];

  var F = FORM;
  var STYLE = [
    F + ' .rp-logo{position:relative;z-index:2;font-family:inherit;line-height:1;white-space:nowrap;color:#111;letter-spacing:.08em}',
    F + ' .rp-logo b{font-weight:600}',
    F + ' .rp-logo span{font-weight:300;margin-left:.25em}',
    F + ' .rp-center{position:relative;z-index:2;display:flex;flex-direction:column;align-items:center;justify-content:center;flex:1;width:100%;text-align:center}',
    F + ' .rp-value{font-family:inherit;font-weight:300;letter-spacing:-.05em;line-height:.9;white-space:nowrap;color:#b05242}',
    F + ' .rp-label,' + F + ' .rp-sub{font-family:inherit;font-weight:500;line-height:1.15;color:#111;letter-spacing:.03em}',
    F + ' .rp-sub{font-weight:400}',
    F + ' .rp-icon{color:#222}',
    F + ' .rp-icon svg{display:block;width:100%;height:auto}',
    // The flip card.
    F + ' .rn-flip-btn{position:relative!important;display:block!important;width:90px!important;height:130px!important;min-width:90px!important;min-height:130px!important;max-width:90px!important;max-height:130px!important;padding:0!important;margin:0 auto!important;border:0!important;background:transparent!important;perspective:900px;transform-style:preserve-3d;overflow:visible!important}',
    F + ' .rn-flip-front{position:absolute!important;inset:0!important;display:block!important;width:90px!important;height:130px!important;object-fit:cover!important;margin:0!important;padding:0!important;transform:rotateY(0);backface-visibility:hidden;-webkit-backface-visibility:hidden;transition:transform .6s cubic-bezier(.2,.7,.2,1)}',
    F + ' .rn-flip-back{box-sizing:border-box!important;position:absolute!important;inset:0!important;display:flex;flex-direction:column;align-items:center;width:90px!important;height:130px!important;padding:9px 6px 6px!important;margin:0!important;background:#fbfbf9;border:1px solid #d4d1c8;border-radius:10px;overflow:hidden;transform:rotateY(-180deg);backface-visibility:hidden;-webkit-backface-visibility:hidden;transition:transform .6s cubic-bezier(.2,.7,.2,1);pointer-events:none}',
    F + ' .rn-flip-back:before,' + F + ' .rn-display-card:before{content:"";position:absolute;border:1px solid #d4d1c8;pointer-events:none}',
    F + ' .rn-flip-back:before{inset:5px 6px;border-radius:8px}',
    F + ' .rn-flip-back .rp-logo{margin-top:8px;font-size:5.5px}',
    F + ' .rn-flip-back .rp-value{font-size:27px}',
    F + ' .rn-flip-back .rp-label{margin-top:4px;font-size:6px;padding:0 4px}',
    F + ' .rn-flip-back .rp-sub{margin-top:2px;font-size:5.2px}',
    F + ' .rn-flip-back .rp-icon{margin-top:7px;width:15px}',
    F + ' .rn-flip-back .rp-sphere{width:12px}',
    F + ' .rn-flip-btn.rn-revealed .rn-flip-front{transform:rotateY(180deg)}',
    F + ' .rn-flip-btn.rn-revealed .rn-flip-back{transform:rotateY(0);pointer-events:auto}',
    // The success-step summary.
    F + ' .rn-prize-set{box-sizing:border-box;display:flex;width:100%;justify-content:center;align-items:center;gap:8px;margin:0 auto}',
    F + ' .rn-display-card{box-sizing:border-box;position:relative;width:140px;height:202px;flex:0 0 140px;display:flex;flex-direction:column;align-items:center;padding:14px 10px 10px;background:#fbfbf9;border:1px solid #d4d1c8;border-radius:15px;overflow:hidden;color:#111}',
    F + ' .rn-display-card:before{inset:8px 9px;border-radius:12px}',
    F + ' .rn-display-card .rp-logo{margin-top:14px;font-size:8px}',
    F + ' .rn-display-card .rp-value{font-size:42px}',
    F + ' .rn-display-card .rp-label{margin-top:6px;font-size:9px;padding:0 6px}',
    F + ' .rn-display-card .rp-sub{margin-top:3px;font-size:8px}',
    F + ' .rn-display-card .rp-icon{margin-top:12px;width:24px}',
    F + ' .rn-display-card .rp-sphere{width:19px}',
    '@media(max-width:767px){' +
      F + ' .rn-prize-set{gap:4px}' +
      F + ' .rn-display-card{width:104px;height:150px;flex-basis:104px;padding:10px 6px 8px;border-radius:11px}' +
      F + ' .rn-display-card:before{inset:6px 7px;border-radius:9px}' +
      F + ' .rn-display-card .rp-logo{margin-top:10px;font-size:6px}' +
      F + ' .rn-display-card .rp-value{font-size:31px}' +
      F + ' .rn-display-card .rp-label{font-size:6.8px;margin-top:4px}' +
      F + ' .rn-display-card .rp-sub{font-size:6px}' +
      F + ' .rn-display-card .rp-icon{margin-top:8px;width:18px}' +
      F + ' .rn-display-card .rp-sphere{width:14px}' +
    '}',
    '@media(prefers-reduced-motion:reduce){' + F + ' .rn-flip-front,' + F + ' .rn-flip-back{transition:none}}',
  ].join('');

  var states = new WeakMap();
  /* Set once the third pick has advanced the form; gates the summary swap, so
     a "Your Prize" image showing up for any other reason is left alone. */
  var advanced = false;
  var styled = false;

  function addStyle() {
    if (styled) return;
    styled = true;
    var s = document.createElement('style');
    s.id = 'rn-klaviyo-pick3';
    s.textContent = STYLE;
    document.head.appendChild(s);
  }

  function imageButtons(form) {
    return Array.prototype.filter.call(form.querySelectorAll('button'), function (b) {
      return !!b.querySelector('img[src]');
    });
  }

  /* A card is an image button on a step showing at least three of them. */
  function isCard(form, button) {
    return !!button.querySelector('img[src]') && imageButtons(form).length >= PICKS_REQUIRED;
  }

  function updateCounter(form, count) {
    form.querySelectorAll('.klaviyo-form-richtext').forEach(function (el) {
      COUNTER_RE.lastIndex = 0;
      if (!COUNTER_RE.test(el.textContent || '')) return;
      COUNTER_RE.lastIndex = 0;
      el.innerHTML = el.innerHTML.replace(COUNTER_RE, 'You’ve won ' + count + ' / 3 offers');
    });
    COUNTER_RE.lastIndex = 0;
  }

  function pin(el, size, props) {
    props.forEach(function (p) { el.style.setProperty(p, size, 'important'); });
  }

  function flipCard(button, index) {
    var img = button.querySelector('img[src]');
    if (!img) return;
    /* Klaviyo sizes these inline from the form design, so the flip size is set
       inline too rather than left to which !important rule wins. */
    pin(button, '90px', ['width', 'min-width', 'max-width']);
    pin(button, '130px', ['height', 'min-height', 'max-height']);
    var cell = button.closest('[data-testid="form-component"]');
    if (cell) {
      pin(cell, '90px', ['width', 'min-width', 'max-width']);
      cell.style.setProperty('flex', '0 0 90px', 'important');
    }
    button.classList.add('rn-flip-btn');
    img.classList.add('rn-flip-front');
    var back = document.createElement('div');
    // `needsclick` keeps Klaviyo's own fastclick handling off this subtree.
    back.className = 'needsclick rn-flip-back';
    back.innerHTML = PRIZES[index] || '';
    button.appendChild(back);
    /* Two frames: the class that animates the flip has to land after the back
       face is painted in its pre-flip state, or no flip is seen. */
    requestAnimationFrame(function () {
      requestAnimationFrame(function () { button.classList.add('rn-revealed'); });
    });
  }

  function advance(state, button) {
    if (state.advancing) return;
    state.advancing = true;
    advanced = true;
    if (state.timer != null) { clearTimeout(state.timer); state.timer = null; }
    /* Reaches Klaviyo's handler as a normal click, since `advancing` makes ours
       stand aside. Lowered straight after, so a card still on screen cannot
       move the form on a second time. */
    try { button.click(); } finally { state.advancing = false; }
    /* The step change is a DOM mutation, which the observer below catches; this
       covers a success step that was already mounted, so nothing mutates. */
    renderSummary();
  }

  function renderSummary() {
    if (!advanced) return;
    document.querySelectorAll(FORM).forEach(function (form) {
      form.querySelectorAll('img[alt="' + SUMMARY_IMAGE_ALT + '"]').forEach(function (img) {
        if (img.dataset.rnPrizeReplaced) return;
        img.dataset.rnPrizeReplaced = '1';
        var holder = img.closest('.klaviyo-form-image');
        if (!holder) return;
        var set = document.createElement('div');
        set.className = 'needsclick rn-prize-set';
        for (var i = 0; i < PICKS_REQUIRED; i++) {
          var card = document.createElement('div');
          card.className = 'needsclick rn-display-card';
          card.innerHTML = PRIZES[i] || '';
          set.appendChild(card);
        }
        holder.innerHTML = '';
        holder.appendChild(set);
        Object.assign(holder.style, { width: '100%', display: 'flex', justifyContent: 'center', overflow: 'visible' });
        // The row above it clips the cards otherwise.
        var cell = holder.closest('[data-testid="form-component"]');
        if (cell) Object.assign(cell.style, { display: 'flex', justifyContent: 'center', width: '100%', overflow: 'visible' });
      });
    });
  }

  function onClick(event) {
    var target = event.target;
    var button = target && target.closest ? target.closest('button') : null;
    if (!button) return;
    var form = button.closest(FORM);
    if (!form || !isCard(form, button)) return;

    var state = states.get(form);
    // Standing aside so the re-dispatched third click reaches Klaviyo.
    if (state && state.advancing) return;
    if (!state) {
      state = { picked: new Set(), count: 0, timer: null, advancing: false };
      states.set(form, state);
      addStyle();
      // The form may ship any starting number; normalise it.
      updateCounter(form, 0);
    }

    // Every card click is ours to swallow; Klaviyo would move the step on.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    // A repeat click on a flipped card, or any click once all three are
    // picked, does nothing else.
    if (state.picked.has(button) || state.count >= PICKS_REQUIRED) return;

    var index = state.count;
    state.picked.add(button);
    state.count += 1;
    updateCounter(form, state.count);
    flipCard(button, index);

    if (state.count === PICKS_REQUIRED) {
      var s = state;
      state.timer = setTimeout(function () { advance(s, button); }, ADVANCE_DELAY_MS);
    }
  }

  // Capture phase: Klaviyo's listener is on the button and would run first.
  window.addEventListener('click', onClick, true);

  /* The form mounts, re-renders and changes step on Klaviyo's schedule, so
     there is no element to scope this to. renderSummary() returns at once
     until the third pick has happened, which keeps the common case cheap. */
  new MutationObserver(renderSummary).observe(document.documentElement, { childList: true, subtree: true });
})();
